/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { modifier } from 'ember-modifier';
import { registerDestructor } from '@ember/destroyable';

// Snowfall effect configuration
const SNOWFLAKE_COUNT = 20;
// snowballs have a size treshold
const SNOWFLAKE_MIN_SIZE = 2;
const SNOWFLAKE_MAX_SIZE = 5;
// they can also fall at a speed between min and max.
const SNOWFLAKE_MIN_SPEED = 0.5;
const SNOWFLAKE_MAX_SPEED = 2;
// new! wind now exists!
const SNOWFLAKE_MIN_WIND = -0.5;
const SNOWFLAKE_MAX_WIND = 0.5;

const MAX_DRIFT_HEIGHT = 8;
const WAVE_FREQUENCY = 0.015;
let SNOWFALL_CANVAS_OFFSET = 0;

export default class NavSidebarSnowfallComponent extends Component {
  @service('browser/window') window;

  // This array holds the references to all the snowflakes
  snowflakes = [];
  animationId = null;
  canvas = null;
  ctx = null;
  sidebarResizeObserver = null;
  previousSidebarWidth = null;

  initSnowfall = modifier((element) => {
    if (!element) return;
    if (this.args.isElectron) {
      SNOWFALL_CANVAS_OFFSET = 30;
    }
    this.canvas = element;
    this.ctx = element.getContext('2d');

    // Set canvas size to be the same as the sidebar
    const sidebar = document.getElementById('sidebar');
    if (sidebar) {
      const rect = sidebar.getBoundingClientRect();
      this.canvas.width = rect.width;
      this.canvas.height = this.window.innerHeight - SNOWFALL_CANVAS_OFFSET;
      this.previousSidebarWidth = rect.width;

      // resize the canvas whenever there is a change to the sidebar width
      if (this.window.ResizeObserver) {
        const handleCanvasResize = () => {
          this.updateCanvasSize();
          this.adjustSnowflakesForWidthChange();
        };
        this.sidebarResizeObserver = new ResizeObserver(() => {
          handleCanvasResize();
        });
        this.sidebarResizeObserver.observe(sidebar);
      }
    } else {
      // this is a fallback for if we use another container for this component.
      this.canvas.width = this.window.innerWidth;
      this.canvas.height = this.window.innerHeight - SNOWFALL_CANVAS_OFFSET;
    }

    // Initialize snowflakes
    this.snowflakes = [];
    for (let i = 0; i < SNOWFLAKE_COUNT; i++) {
      this.snowflakes.push(this.createSnowflake());
    }

    // Start animation
    this.animate();

    // Handle window resize
    this.window.addEventListener('resize', this.handleResize);
    registerDestructor(this, this.destroySnowfall);
  });

  @action
  handleResize() {
    if (this.canvas) {
      this.updateCanvasSize();
    }
  }

  updateCanvasSize() {
    if (!this.canvas) return;
    const sidebar = document.getElementById('sidebar');
    if (sidebar) {
      const rect = sidebar.getBoundingClientRect();
      this.canvas.width = rect.width;
      this.canvas.height = this.window.innerHeight - SNOWFALL_CANVAS_OFFSET;
      // Update previous width for tracking changes
      if (!this.previousSidebarWidth) {
        this.previousSidebarWidth = rect.width;
      }
    } else {
      this.canvas.width = this.window.innerWidth;
      this.canvas.height = this.window.innerHeight - SNOWFALL_CANVAS_OFFSET;
    }
  }
  // This triggers when the sidebar is resized. it'll adjust the snowflakes to stay within the new bounds.
  // yes, this was "borrowed"
  adjustSnowflakesForWidthChange() {
    const sidebar = document.getElementById('sidebar');
    if (!sidebar || !this.snowflakes.length) return;

    const currentWidth = sidebar.getBoundingClientRect().width;
    if (
      this.previousSidebarWidth &&
      currentWidth !== this.previousSidebarWidth
    ) {
      // Adjust snowflake positions to stay within new bounds
      const widthRatio = currentWidth / this.previousSidebarWidth;

      this.snowflakes.forEach((flake) => {
        // Scale x position proportionally
        flake.x = flake.x * widthRatio;

        // Ensure snowflakes stay within bounds
        if (flake.x < 0) {
          flake.x = 0;
        } else if (flake.x > currentWidth) {
          flake.x = currentWidth;
        }
      });

      this.previousSidebarWidth = currentWidth;
    }
  }

  @action
  destroySnowfall() {
    // Stop animation
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }

    // Remove resize observer
    if (this.sidebarResizeObserver) {
      const sidebar = document.getElementById('sidebar');
      if (sidebar) {
        this.sidebarResizeObserver.unobserve(sidebar);
      }
      this.sidebarResizeObserver = null;
    }

    // Remove resize listener
    this.window.removeEventListener('resize', this.handleResize);

    // Clear canvas
    if (this.ctx && this.canvas) {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }

    // Reset references
    this.canvas = null;
    this.ctx = null;
    this.snowflakes = [];
    this.previousSidebarWidth = null;
  }

  createSnowflake() {
    const width = this.canvas?.width || this.window.innerWidth;

    return {
      x: Math.random() * width,
      y: Math.random() * this.window.innerHeight,
      // the size of the snowflake is a random number between the min and max size
      size:
        Math.random() * (SNOWFLAKE_MAX_SIZE - SNOWFLAKE_MIN_SIZE) +
        SNOWFLAKE_MIN_SIZE,
      speed:
        Math.random() * (SNOWFLAKE_MAX_SPEED - SNOWFLAKE_MIN_SPEED) +
        SNOWFLAKE_MIN_SPEED,
      wind:
        Math.random() * (SNOWFLAKE_MAX_WIND - SNOWFLAKE_MIN_WIND) +
        SNOWFLAKE_MIN_WIND,
      opacity: Math.random() * 0.5 + 0.5,
      initialOpacity: Math.random() * 0.5 + 0.5,
      landed: false,
      landedX: null,
      landedY: null,
      landedTime: null,
    };
  }

  #getDriftY(x, barY) {
    const wave1 = Math.sin(x * WAVE_FREQUENCY) * MAX_DRIFT_HEIGHT;
    const wave2 = Math.sin(x * WAVE_FREQUENCY * 2.3) * (MAX_DRIFT_HEIGHT * 0.6);
    const wave3 = Math.sin(x * WAVE_FREQUENCY * 0.7) * (MAX_DRIFT_HEIGHT * 0.4);

    return barY - (wave1 + wave2 + wave3);
  }

  #checkCollision(flake, barY) {
    if (!barY || flake.landed) return false;

    // Check if snowflake has hit the wavy snow drift
    const flakeRadius = flake.size;
    const flakeBottom = flake.y + flakeRadius;
    const driftY = this.#getDriftY(flake.x, barY);

    // Check if snowflake bottom has reached or passed the drift surface
    return flakeBottom >= driftY;
  }

  animate() {
    // Clear canvas
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    const width = this.canvas.width;
    const height = this.canvas.height;
    const barDepth = 3;
    const barY = height - barDepth; // Top of the white bar

    // First, update positions and check for collisions
    this.snowflakes.forEach((flake) => {
      if (!flake.landed) {
        // Update position
        flake.y += flake.speed;
        flake.x += flake.wind;

        // Check for collision with wavy snow drift at bottom of sidebar
        if (this.#checkCollision(flake, barY)) {
          flake.landed = true;
          // Land on top of snow drift at this X position
          flake.landedY = this.#getDriftY(flake.x, barY);
          flake.landedX = flake.x;
          // Start fade-out timer (3 seconds = 3000ms)
          flake.landedTime = Date.now();
          flake.initialOpacity = flake.opacity;
        }

        // Reset if snowflake goes off screen
        if (flake.y > height) {
          flake.y = -10;
          flake.x = Math.random() * width;
        }

        // Wrap around horizontally
        if (flake.x < 0) {
          flake.x = width;
        } else if (flake.x > width) {
          flake.x = 0;
        }
      }
    });

    // Draw wavy snow drift at bottom of sidebar
    const sidebar = document.getElementById('sidebar');
    if (sidebar) {
      this.ctx.fillStyle = 'rgba(255, 255, 255, 1)';
      this.ctx.beginPath();

      // Start from bottom-left
      this.ctx.moveTo(0, height);

      // Create wavy path using sine waves with multiple frequencies for natural look
      for (let x = 0; x <= width; x += 2) {
        // Combine multiple sine waves for more natural variation
        const wave1 = Math.sin(x * WAVE_FREQUENCY) * MAX_DRIFT_HEIGHT;
        const wave2 =
          Math.sin(x * WAVE_FREQUENCY * 2.3) * (MAX_DRIFT_HEIGHT * 0.6);
        const wave3 =
          Math.sin(x * WAVE_FREQUENCY * 0.7) * (MAX_DRIFT_HEIGHT * 0.4);

        const y = barY - (wave1 + wave2 + wave3);
        this.ctx.lineTo(x, y);
      }

      // Complete the path: down to bottom-right, then back along bottom
      this.ctx.lineTo(width, height);
      this.ctx.lineTo(0, height);
      this.ctx.closePath();
      this.ctx.fill();
    }

    // Update and draw all snowflakes
    const currentTime = Date.now();
    const fadeDuration = 3000; // 3 seconds in milliseconds

    this.snowflakes.forEach((flake) => {
      if (flake.landed && flake.landedTime !== null) {
        // Update fade-out opacity
        const elapsed = currentTime - flake.landedTime;
        const fadeProgress = Math.min(elapsed / fadeDuration, 1);
        flake.opacity = flake.initialOpacity * (1 - fadeProgress);

        // Remove snowflake if fully faded out
        if (fadeProgress >= 1) {
          // Reset snowflake instead of removing to maintain count
          flake.landed = false;
          flake.landedTime = null;
          flake.y = -10;
          flake.x = Math.random() * width;
          flake.opacity = Math.random() * 0.5 + 0.5;
          flake.initialOpacity = flake.opacity;
          return;
        }

        // Draw snowflake at landing position (fading out)
        if (flake.landedX !== undefined && flake.landedY !== null) {
          this.ctx.beginPath();
          this.ctx.arc(
            flake.landedX,
            flake.landedY,
            flake.size,
            0,
            Math.PI * 2
          );
          this.ctx.fillStyle = `rgba(255, 255, 255, ${flake.opacity})`;
          this.ctx.fill();
        }
      } else if (!flake.landed) {
        // Draw snowflake
        this.ctx.beginPath();
        this.ctx.arc(flake.x, flake.y, flake.size, 0, Math.PI * 2);
        this.ctx.fillStyle = `rgba(255, 255, 255, ${flake.opacity})`;
        this.ctx.fill();
      }
    });

    // Continue animation
    this.animationId = requestAnimationFrame(() => this.animate());
  }
}
